// ============================================================
// Team + audit log — response types and pure helpers. Shapes mirror
// packages/api/src/routes/v2/team.ts (snake_case like the rest of v2).
// Side-effect free (unit tested in test/team.test.ts).
// ============================================================

import { canManageOrg, type AssignableOrgRole, type OrgRole } from '@yourownmetric/shared'

export interface OrgSummary {
    id: string
    name: string
    slug: string
    plan: string
    role: OrgRole
    can_manage: boolean
    members: number
    pending_invites: number
    /** null = unlimited */
    member_limit: number | null
    /** Pay-as-you-go feature; Free orgs see an upgrade prompt. */
    audit_log_available: boolean
    /** Org policy: every member must have two-step verification on. */
    require_mfa: boolean
    /** A partner created this org and someone claimed it: Settings > Access applies. */
    partner_managed?: boolean
}

/** GET /v2/orgs/current/partner-access (owners and admins). */
export type PartnerAccess =
    | { status: 'none'; partner: null }
    | {
          status: 'active' | 'revoked'
          partner: { org_id: string; name: string }
          claimed: boolean
          granted_at: string
          scopes: string[]
          last_used_at: string | null
          revoked_at: string | null
          revoked_by: { id: string; email: string | null; name: string | null } | null
          can_revoke: boolean
      }

/** What a partner key scope lets the partner do, in plain words. */
export function partnerScopeLabel(scope: string): string {
    if (scope === 'orgs:write') {
        return 'Set up and rename this organization and its projects'
    }
    if (scope === 'orgs:delegate') {
        return 'Show your reports in its own product, send events for your sites and read daily totals'
    }
    return scope
}

/** The partner's name as data says it; never a hardcoded customer name. */
export function partnerName(a: PartnerAccess): string {
    return a.partner?.name.trim() || 'Your partner'
}

/** Body of the "remove access" confirmation. */
export function revokeConsequences(name: string): string[] {
    return [
        `${name} will no longer show your analytics inside its dashboard.`,
        `${name} can't change this organization, add projects or send events for your sites.`,
        'You keep all your data, projects and API keys.',
        `You can't undo this. To work with ${name} again, they need to set you up again.`,
    ]
}

export interface TeamMember {
    user_id: string
    email: string
    name: string
    role: OrgRole
    joined_at: string
    mfa_enabled: boolean
    last_login_at: string | null
    is_you: boolean
}

export interface TeamInvite {
    id: string
    email: string
    role: AssignableOrgRole
    status: 'pending' | 'accepted' | 'revoked' | 'expired'
    invited_by: { id: string; name: string | null; email: string | null } | null
    expires_at: string
    created_at: string
}

export interface IssuedInvite extends TeamInvite {
    invite_url: string
    emailed: boolean
}

export interface AuditEntry {
    id: string
    at: string
    action: string
    resource_type: string
    resource_id: string | null
    actor: { type: string; id: string; email: string | null; name: string | null }
    ip_hash: string | null
    details: Record<string, unknown>
}

export interface AuditPage {
    entries: AuditEntry[]
    next_cursor: string | null
    retention_days: number
}

export interface AuditFilterInput {
    from?: string
    to?: string
    action?: string
    actor?: string
    cursor?: string | null
    limit?: number
}

export interface InvitePreview {
    org_name: string
    email: string
    role: AssignableOrgRole
    inviter_name: string | null
    expires_at: string
    status: TeamInvite['status']
    account_exists: boolean
}

export const ROLE_LABELS: Record<OrgRole, string> = {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    viewer: 'Viewer',
}

export const ROLE_HELP: Record<OrgRole, string> = {
    owner: 'Everything, including billing and transferring the organization',
    admin: 'Manage members, keys, billing and all settings',
    member: 'Read analytics and change analytics settings (goals, funnels, destinations…)',
    viewer: 'Read-only access to analytics',
}

/** Roles `actor` may hand out (invite or change to): only the owner grants admin. */
export function grantableRoles(actor: OrgRole): AssignableOrgRole[] {
    if (actor === 'owner') {
        return ['admin', 'member', 'viewer']
    }
    return canManageOrg(actor) ? ['member', 'viewer'] : []
}

/** Whether `actor` may change or remove `target` (mirrors services/team.ts rules). */
export function canEditMember(actor: OrgRole, target: TeamMember): boolean {
    if (target.role === 'owner' || target.is_you) {
        return false
    }
    if (actor === 'owner') {
        return true
    }
    return actor === 'admin' && target.role !== 'admin'
}

/** "3 of 1" style seat summary; null limit = unlimited. */
export function seatSummary(o: Pick<OrgSummary, 'members' | 'pending_invites' | 'member_limit'>): {
    label: string
    full: boolean
} {
    const used = o.members + o.pending_invites
    if (o.member_limit === null) {
        return {
            label: `${o.members} member${o.members === 1 ? '' : 's'} · unlimited seats`,
            full: false,
        }
    }
    return {
        label: `${used} of ${o.member_limit} seat${o.member_limit === 1 ? '' : 's'} used`,
        full: used >= o.member_limit,
    }
}

/** Query string for GET /orgs/current/audit-logs (empty values dropped). */
export function auditQuery(f: AuditFilterInput, extra: Record<string, string> = {}): string {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries({ ...f, ...extra })) {
        if (v !== undefined && v !== null && v !== '') {
            q.set(k, String(v))
        }
    }
    return q.toString()
}

/** "member.role_change" → "Member role change". */
export function actionLabel(action: string): string {
    const s = action.replace(/[._]/g, ' ').trim()
    return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Who did it, for a table cell. */
export function actorLabel(a: AuditEntry['actor']): string {
    if (a.type === 'user') {
        return a.email ?? a.name ?? a.id
    }
    if (a.type === 'management_key') {
        return `Management key ${a.id}`
    }
    if (a.type === 'api_key') {
        return `API key ${a.id}`
    }
    if (a.type === 'system') {
        return a.id === 'billing' ? 'Billing system' : 'System'
    }
    if (a.type === 'staff') {
        return 'YOM staff'
    }
    if (a.type === 'portal_user') {
        return `Client login ${a.id}`
    }
    return a.id || a.type
}

/** Action families for the audit-log filter (prefix filters end with '.'). */
export const AUDIT_ACTION_FILTERS: Array<{ value: string; label: string }> = [
    { value: '', label: 'All actions' },
    { value: 'member.', label: 'Members and invites' },
    { value: 'user.', label: 'Sign-ins and passwords' },
    { value: 'mfa.', label: 'Multi-factor authentication' },
    { value: 'api_key.', label: 'API keys' },
    { value: 'management_key.', label: 'Management keys' },
    { value: 'project.', label: 'Projects' },
    { value: 'destination.', label: 'Destinations' },
    { value: 'export.', label: 'Warehouse exports' },
    { value: 'portal.', label: 'Client portals' },
    { value: 'portal_user.', label: 'Client logins' },
    { value: 'plan.', label: 'Plan changes' },
    { value: 'org.', label: 'Organization' },
    { value: 'admin.', label: 'YOM staff actions' },
]
